import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Navbar from '../components/Navbar'
import ImageUpload from '../components/ImageUpload'
import LoginModal from '../components/LoginModal'
import AcknowledgeModal from '../components/AcknowledgeModal'
import { supabase } from '../lib/supabase'
import { flagFields } from '../lib/profanity'
import { geocodeListing } from '../lib/geocode'
import { useAuth } from '../lib/AuthContext'

export default function SubmitPage() {
  const { user } = useAuth()
  const navigate  = useNavigate()
  const [loginPrompt, setLoginPrompt] = useState(!user)
  const [form, setForm] = useState({
    name:'', category:'club', discipline:'multi', region:'North America',
    city:'', country:'', type_label:'', description:'', address:'',
    website_url:'', facebook_url:'', instagram_url:'', image_url:'',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError]     = useState('')
  const [done, setDone]       = useState(false)
  const [showAck, setShowAck] = useState(false)

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const [dupWarning, setDupWarning] = useState(null)  // { name, city } of potential duplicate

  // Step 1: validate then check for duplicates then show acknowledge modal
  async function handleSubmitClick(e) {
    e.preventDefault()
    setError('')
    if (!form.name.trim() || !form.description.trim() || !form.city.trim()) {
      setError('Please fill in Name, City, and Description.'); return
    }

    // Duplicate check — fuzzy match on name + city
    const { data: existing } = await supabase
      .from('listings')
      .select('id, name, city')
      .ilike('name', `%${form.name.trim().split(' ')[0]}%`)  // match first word of name
      .ilike('city', `%${form.city.trim().split(',')[0]}%`)  // match first part of city
      .limit(3)

    if (existing && existing.length > 0) {
      setDupWarning(existing[0])
      return  // show warning first, user must confirm or proceed
    }

    setShowAck(true)
  }

  function proceedDespiteDuplicate() {
    setDupWarning(null)
    setShowAck(true)
  }

  // Step 2: user acknowledged — actually save to DB
  async function handleConfirmedSubmit() {
    setShowAck(false)
    const flagged = flagFields(form)
    setLoading(true)

    // Geocode for globe map — non-blocking, listing publishes regardless
    let coords = { lat: null, lng: null }
    try {
      const result = await geocodeListing({
        address: form.address,
        city:    form.city,
        country: form.country,
      })
      if (result) coords = result
    } catch (_) {}

    const { error: dbErr } = await supabase.from('listings').insert({
      ...form,
      lat:         coords.lat,
      lng:         coords.lng,
      status:      flagged ? 'flagged' : 'pending',
      is_featured: false,
      is_verified: false,
      vote_count:  0,
      submitted_by: user.id,
    })

    if (!dbErr) {
      // Fire email notification to listings@windsportsdirectory.com
      try {
        await supabase.functions.invoke('notify-submission', {
          body: {
            type: 'listing',
            name: form.name,
            city: form.city,
            country: form.country,
            category: form.category,
            flagged,
          }
        })
      } catch (_) { /* don't block on email failure */ }
    }

    setLoading(false)
    if (dbErr) { setError(dbErr.message); return }
    if (flagged) {
      setError('Your listing was submitted but flagged for review due to potentially inappropriate language.')
    } else {
      setDone(true)
    }
  }

  // Login wall — non-members can browse but not submit
  if (!user) return (
    <div>
      <Navbar />
      <div style={{ maxWidth: 480, margin: '80px auto', textAlign: 'center', padding: '0 20px' }}>
        <img src="/logo.png" alt="Wind Sports Directory" style={{ height: 64, width: 'auto', margin: '0 auto 20px', display: 'block' }} />
        <h2 style={{ fontSize: 24, fontWeight: 800, marginBottom: 10 }}>Members Only</h2>
        <p style={{ color: 'var(--gray-500)', fontSize: 15, lineHeight: 1.6, marginBottom: 28 }}>
          You need a free account to submit a club, school, or shop listing.<br/>
          Everything else on the site is open to everyone.
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          <LoginModal onClose={() => setLoginPrompt(false)} inline />
        </div>
        <p style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 20 }}>
          Already have an account? Click Sign In above.
        </p>
      </div>
    </div>
  )

  if (done) return (
    <div>
      <Navbar />
      <div style={{ maxWidth:520, margin:'80px auto', textAlign:'center', padding:'0 20px' }}>
        <div style={{ fontSize:56, marginBottom:16 }}>🎉</div>
        <h2 style={{ fontSize:24, fontWeight:800, marginBottom:8 }}>Listing Published!</h2>
        <p style={{ color:'var(--gray-500)', marginBottom:24 }}>
          Your listing has been automatically added to the directory. An admin may review it later and can remove it if needed.
        </p>
        <div style={{ display:'flex', gap:10, justifyContent:'center' }}>
          <button onClick={() => navigate('/directory')} className="btn btn-primary">View Directory</button>
          <button onClick={() => { setDone(false); setForm({ name:'', category:'club', discipline:'multi', region:'North America', city:'', country:'', type_label:'', description:'', website_url:'', facebook_url:'', instagram_url:'', image_url:'' }) }} className="btn btn-ghost">Submit Another</button>
        </div>
      </div>
    </div>
  )

  return (
    <div>
      <Navbar />
      <div className="page-wrap" style={{ maxWidth:640 }}>
        <div className="page-title">Submit a Listing</div>
        <div className="page-sub">
          Clubs, schools, shops, and associations welcome. Listings auto-publish instantly — admins review later.
        </div>

        {error && (
          <div style={{ background:'#fee2e2', color:'#dc2626', padding:'12px 16px', borderRadius:8, fontSize:13, marginBottom:20 }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmitClick}>
          <div className='form-grid-2' style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
            <div className="field" style={{ gridColumn:'1/-1' }}>
              <label>Club / School / Shop Name *</label>
              <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Pacific Kite Co." required />
            </div>

            <div className="field">
              <label>Category *</label>
              <select value={form.category} onChange={e => set('category', e.target.value)}>
                <option value="club">Club</option>
                <option value="school">School</option>
                <option value="shop">Shop</option>
                <option value="association">Association</option>
                <option value="online">Online Community</option>
              </select>
            </div>

            <div className="field">
              <label>Discipline *</label>
              <select value={form.discipline} onChange={e => set('discipline', e.target.value)}>
                <option value="windsurf">Windsurfing</option>
                <option value="kite">Kiteboarding</option>
                <option value="wing">Wingfoil</option>
                <option value="windfoil">Windfoil</option>
                <option value="multi">Multi-discipline</option>
              </select>
            </div>

            <div className="field">
              <label>City *</label>
              <input value={form.city} onChange={e => set('city', e.target.value)} placeholder="Hood River, OR" required />
            </div>

            <div className="field">
              <label>Country *</label>
              <input value={form.country} onChange={e => set('country', e.target.value)} placeholder="USA" required />
            </div>

            <div className="field" style={{ gridColumn:'1/-1' }}>
              <label>Region</label>
              <select value={form.region} onChange={e => set('region', e.target.value)}>
                {['North America','South America','Europe','Africa & Middle East','Caribbean','Pacific & Oceania','Asia','Other'].map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
            </div>

            <div className="field" style={{ gridColumn:'1/-1' }}>
              <label>
                Street Address
                <span style={{ fontWeight:400, color:'var(--gray-400)', marginLeft:6 }}>(optional — used for map pin · never shown publicly without your consent)</span>
              </label>
              <input
                value={form.address || ''}
                onChange={e => set('address', e.target.value)}
                placeholder="123 Main St, Hood River, OR 97031"
              />
              <div style={{ fontSize:11, color:'var(--gray-400)', marginTop:4, display:'flex', alignItems:'center', gap:5 }}>
                🌍 If blank, your pin will appear at the city center on the Globe view. An address gives a more precise location.
              </div>
            </div>

            <div className="field" style={{ gridColumn:'1/-1' }}>
              <label>Short type label <span style={{ fontWeight:400, color:'var(--gray-400)' }}>(e.g. "School & Shop · Columbia Gorge")</span></label>
              <input value={form.type_label} onChange={e => set('type_label', e.target.value)} placeholder="School · Pacific Northwest" />
            </div>

            <div className="field" style={{ gridColumn:'1/-1' }}>
              <label>Description * <span style={{ fontWeight:400, color:'var(--gray-400)' }}>(2–4 sentences)</span></label>
              <textarea value={form.description} onChange={e => set('description', e.target.value)} placeholder="Tell people what makes this place special — wind conditions, specialties, who it's for…" required />
            </div>

            <div className="field" style={{ gridColumn:'1/-1' }}>
              <label>Website URL</label>
              <input type="url" value={form.website_url} onChange={e => set('website_url', e.target.value)} placeholder="https://…" />
            </div>

            <div className="field">
              <label>Facebook URL</label>
              <input type="url" value={form.facebook_url} onChange={e => set('facebook_url', e.target.value)} placeholder="https://facebook.com/…" />
            </div>

            <div className="field">
              <label>Instagram URL</label>
              <input type="url" value={form.instagram_url} onChange={e => set('instagram_url', e.target.value)} placeholder="https://instagram.com/…" />
            </div>

            <div className="field" style={{ gridColumn:'1/-1' }}>
              <label>Listing Photo <span style={{ fontWeight:400, color:'var(--gray-400)' }}>(optional — upload or paste URL)</span></label>
              <ImageUpload value={form.image_url} onChange={v => set('image_url', v)} />
            </div>
          </div>

          {/* Featured upsell */}
          <div style={{ background:'linear-gradient(135deg, #fffbeb, #fef3c7)', border:'1.5px solid var(--gold)', borderRadius:10, padding:'14px 16px', marginBottom:20, display:'flex', alignItems:'center', gap:12 }}>
            <div style={{ fontSize:24 }}>⭐</div>
            <div>
              <div style={{ fontWeight:700, fontSize:13, color:'#92400e' }}>Want a Featured listing?</div>
              <div style={{ fontSize:12, color:'#a16207', marginTop:2 }}>
                Featured listings appear at the top of their region with a gold highlight. Starting at $15/month.
              </div>
            </div>
            <a href="mailto:hello@windsportsdirectory.com?subject=Featured Listing Inquiry"
              style={{ background:'var(--gold)', color:'#fff', padding:'7px 14px', borderRadius:7, fontSize:12, fontWeight:600, textDecoration:'none', whiteSpace:'nowrap', marginLeft:'auto' }}>
              Get Featured
            </a>
          </div>

          <button type="submit" className="btn btn-primary" style={{ width:'100%', padding:'13px' }} disabled={loading}>
            {loading ? 'Submitting…' : '🚀 Publish Listing'}
          </button>
          <p style={{ fontSize:11, color:'var(--gray-400)', textAlign:'center', marginTop:10 }}>
            Listings publish immediately and are visible to everyone. Admins review periodically.
          </p>
        </form>
      </div>

      {/* Duplicate warning modal */}
      {dupWarning && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setDupWarning(null)}>
          <div className="modal-box" style={{ maxWidth: 440, textAlign: 'center' }}>
            <div style={{ fontSize: 44, marginBottom: 14 }}>⚠️</div>
            <h3 style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>Possible duplicate found</h3>
            <p style={{ fontSize: 13, color: 'var(--gray-500)', lineHeight: 1.6, marginBottom: 16 }}>
              A listing called <strong>"{dupWarning.name}"</strong> in <strong>{dupWarning.city}</strong> already exists in the directory. Is your listing different from this one?
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setDupWarning(null)} className="btn btn-ghost" style={{ flex: 1 }}>
                Go Back & Edit
              </button>
              <button onClick={proceedDespiteDuplicate} className="btn btn-primary" style={{ flex: 1 }}>
                It's Different — Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Acknowledge modal */}
      {showAck && (
        <AcknowledgeModal
          type="listing"
          onAcknowledge={handleConfirmedSubmit}
          onCancel={() => setShowAck(false)}
        />
      )}
    </div>
  )
}
